// apps/share-web/src/offline/offline-keys.ts
//
// Ключ офлайн-кэша. Четыре части, первая — ЛИЧНОСТЬ.
//
// Личность в ключе, а не в имени базы, потому что кэш обязан пережить смену
// человека на одном телефоне: без неё второй вошедший читал бы слова первого.
// Это и есть причина, по которой волна не расширяет service worker — его кэш
// адресуется URL'ами и про вошедшего не знает ничего.
//
// Разделитель — U+001F (unit separator): он не встречается ни в UUID, ни в
// идентификаторах команд, и его невозможно ввести в имя песни. Часть,
// содержащая его, отвергается: иначе («u1␟u2», «t1») и («u1», «u2␟t1») дали бы
// ОДИН ключ, то есть один человек получил бы кэш другого.

export const SEP = '\u001f';

// 'me' — дефект 2 живой приёмки волны G: офлайн-кэш вошедшего для шапки
// (ui/app-header.ts). Третий вид после library/words; заводя его, ОБЯЗАН
// был тронуть `parseKey` ниже (иначе `makeKey` типизированно принимает
// 'me', а `parseKey` тихо отвергает собственный же ключ) — комментарий в
// offline/reconcile.ts предсказывал именно этот момент.
//
// 'setlist' — задача C5 волны G: состав ОДНОГО предстоящего сетлиста
// (`GET /api/setlists/:id`, offline/setlist-offline.ts). Тот же урок, что у
// 'me': четвёртый вид ОБЯЗАН тронуть `parseKey` ниже вместе с собой — иначе
// `makeKey('setlist', ...)` типизированно проходит, а `parseKey` тихо
// отвергает собственный же ключ.
export type OfflineKind = 'library' | 'words' | 'me' | 'setlist';

export interface ParsedKey {
  userId: string;
  teamId: string;
  kind: OfflineKind;
  id: string;
}

function rejectIfContainsSeparator(name: string, value: string): void {
  if (value.includes(SEP)) throw new Error(`offline-keys: ${name} contains the separator`);
}

export function makeKey(userId: string, teamId: string, kind: OfflineKind, id: string): string {
  // `.trim()`, а не просто пустота: `String(user?.id)` в проводке даёт строку
  // 'undefined' — её мы отсечь не можем, — но пробельная личность приезжает
  // достаточно часто, чтобы её ловить здесь, а не на телефоне.
  if (!userId.trim()) throw new Error('offline-keys: userId is required');
  rejectIfContainsSeparator('userId', userId);
  rejectIfContainsSeparator('teamId', teamId);
  // Четвёртая часть охраняется наравне с первыми: разделитель в `id` даёт
  // пять кусков, `parseKey` возвращает null, и сверка молча пропускает ключ —
  // отозванные слова остались бы на телефоне навсегда.
  rejectIfContainsSeparator('id', id);
  return `${userId}${SEP}${teamId}${SEP}${kind}${SEP}${id}`;
}

export function parseKey(key: string): ParsedKey | null {
  const parts = key.split(SEP);
  if (parts.length !== 4) return null;
  const [userId, teamId, kind, id] = parts as [string, string, string, string];
  if (kind !== 'library' && kind !== 'words' && kind !== 'me' && kind !== 'setlist') return null;
  return { userId, teamId, kind, id };
}

/** Все ключи пользователя. */
export function userPrefix(userId: string): string {
  return `${userId}${SEP}`;
}

/** Все ключи одной команды этого пользователя. */
export function teamPrefix(userId: string, teamId: string): string {
  return `${userId}${SEP}${teamId}${SEP}`;
}
